import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, CheckCircle2, Play, Loader2, AlertCircle } from 'lucide-react';

import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';

import { useToast } from '../hooks/useToast';
import { client, fetchOrganizations } from '../apiHelper';

export const DepreciationPostingConsole = ({ currentUser }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [posting, setPosting] = useState(false);

    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [periodDate, setPeriodDate] = useState(new Date().toISOString().split('T')[0]);

    const [dueSchedules, setDueSchedules] = useState([]);

    const loadPendingSchedules = useCallback(async () => {
        if (!selectedOrg || !periodDate) return;
        setLoading(true);
        try {
            const data = await client.postData('/accounting/pending-depreciation-schedules', {selectedOrg, periodDate});
            setDueSchedules(Array.isArray(data) ? data : []);
        } catch (err) {
            showToast("Failed to fetch pending depreciation schedules.", "error");
            setDueSchedules([]);
        } finally {
            setLoading(false);
        }
    }, [selectedOrg, periodDate, showToast]);

    useEffect(() => {
        const init = async () => {
            const orgs = await fetchOrganizations().catch(() => []);
            if (orgs.length > 0) {
                const mapped = orgs.map(o => ({ label: o.organization_name, value: o.organization_id.toString() }));
                const myOrg = mapped.find(o => o.value == currentUser.org_id);
                setOrganizations([myOrg]);
                setSelectedOrg(myOrg.value);
            }
        };
        init();
    }, []);

    useEffect(() => {
        loadPendingSchedules();
    }, [loadPendingSchedules]);

    const calculateTotalDepreciation = () => {
        return dueSchedules.reduce((sum, item) => sum + parseFloat(item.depreciation_amount || 0), 0);
    };

    const handleRunBatchDepreciation = async () => {
        if (dueSchedules.length === 0) {
            showToast("No active depreciation schedules eligible for posting.", "error");
            return;
        }

        setPosting(true);
        try {
            const payload = {
                organization_id: parseInt(selectedOrg, 10),
                period_date: periodDate,
                schedule_ids: dueSchedules.map(s => s.schedule_id)
            };

            await client.postData("/fixed-assets/post-depreciation-batch", payload);
            showToast(`Posted depreciation journal entry for ${dueSchedules.length} assets successfully.`, "success");
            loadPendingSchedules();
        } catch (err) {
            showToast(err.response?.data?.detail || "Batch depreciation posting failed.", "error");
        } finally {
            setPosting(false);
        }
    };

    return (
        <Card className="p-6 border-zinc-200/60 shadow-sm bg-white">
            <div className="border-b border-zinc-100 pb-3 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                        <Calendar className="text-indigo-600" size={20} /> Period-End Depreciation Execution
                    </h2>
                    <p className="text-xs text-zinc-500">Post system-calculated depreciation schedules directly to General Ledger accounts.</p>
                </div>
                <div className="flex gap-3">
                    <Button
                        onClick={handleRunBatchDepreciation}
                        disabled={posting || dueSchedules.length === 0}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-5 py-2 font-medium"
                    >
                        {posting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Play size={14} className="mr-1 inline" />} Post Depreciation Journal
                    </Button>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 bg-zinc-50 p-4 rounded-lg border border-zinc-200">
                <SearchableSelectBox
                    label="Target Organization"
                    options={organizations}
                    value={selectedOrg}
                    onChange={(e) => setSelectedOrg(e.target.value)}
                    placeholder="Select Organization..."
                />
                <Input
                    label="Period End Date"
                    type="date"
                    value={periodDate}
                    onChange={(e) => setPeriodDate(e.target.value)}
                />
                <div className="flex flex-col justify-end">
                    <div className="p-2.5 bg-white border border-zinc-200 rounded-lg text-right">
                        <span className="text-[10px] text-zinc-400 font-bold uppercase block">Total Batch Amount</span>
                        <span className="text-lg font-extrabold text-indigo-600">
                            ₦{calculateTotalDepreciation().toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="border rounded-lg overflow-hidden text-xs">
                <table className="w-full text-left">
                    <thead className="bg-zinc-100 text-zinc-600 font-semibold border-b border-zinc-200">
                        <tr>
                            <th className="p-3">Asset Tag</th>
                            <th className="p-3">Asset Name</th>
                            <th className="p-3">Period #</th>
                            <th className="p-3 text-right">Beginning Value</th>
                            <th className="p-3 text-right">Depreciation</th>
                            <th className="p-3 text-right">Ending Book Value</th>
                            <th className="p-3 text-center">GL Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                        {loading ? (
                            <tr>
                                <td colSpan="7" className="p-6 text-center text-zinc-500">
                                    <Loader2 className="animate-spin inline mr-2 text-indigo-600" /> Calculating schedule matrix...
                                </td>
                            </tr>
                        ) : dueSchedules.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="p-6 text-center text-zinc-400">
                                    <CheckCircle2 className="mx-auto text-emerald-500 mb-1" size={24} />
                                    No pending depreciation runs required for this period.
                                </td>
                            </tr>
                        ) : (
                            dueSchedules.map((item, idx) => (
                                <tr key={idx} className="hover:bg-zinc-50/50">
                                    <td className="p-3 font-medium text-zinc-900">{item.asset_number}</td>
                                    <td className="p-3 text-zinc-700">{item.asset_name}</td>
                                    <td className="p-3 font-semibold text-indigo-600">#{item.period_number}</td>
                                    <td className="p-3 text-right">₦{parseFloat(item.beginning_book_value).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                    <td className="p-3 text-right font-bold text-zinc-900">₦{parseFloat(item.depreciation_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                    <td className="p-3 text-right">₦{parseFloat(item.ending_book_value).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                    <td className="p-3 text-center">
                                        <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-[10px] font-medium">Pending Posting</span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};